import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { 
  Mail, 
  Lock, 
  Building2, 
  User, 
  Phone, 
  ArrowRight, 
  Check,
  Shield,
  Briefcase,
  MapPin
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hammer & Anvil Forge Icon
const ForgeIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M6 28H34V32C34 33.1 33.1 34 32 34H8C6.9 34 6 33.1 6 32V28Z" fill="currentColor"/>
    <path d="M4 24H36V28H4V24Z" fill="currentColor"/>
    <path d="M10 20H30V24H10V20Z" fill="currentColor"/>
    <path d="M20 6L24 10L20 14L16 10L20 6Z" fill="#D4AF37"/>
    <path d="M18 12L22 16V20H18V16L18 12Z" fill="#D4AF37"/>
    <circle cx="12" cy="18" r="1" fill="#D4AF37"/>
    <circle cx="28" cy="16" r="1" fill="#D4AF37"/>
  </svg>
);

export default function ContractorRegisterPage({ onContractorLogin }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    business_name: "",
    contact_name: "",
    phone: "",
    service_areas: "",
    specialties: []
  });

  const specialtyOptions = [
    "Kitchen Remodel",
    "Bathroom Remodel",
    "Flooring",
    "Roofing",
    "HVAC",
    "Plumbing",
    "Electrical",
    "Painting",
    "Drywall",
    "Windows/Doors",
    "Foundation",
    "General Contractor"
  ];

  const toggleSpecialty = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (formData.specialties.length === 0) {
      toast.error("Please select at least one specialty");
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/contractor/auth/register`,
        {
          email: formData.email,
          password: formData.password,
          business_name: formData.business_name,
          contact_name: formData.contact_name,
          phone: formData.phone,
          service_areas: formData.service_areas.split(",").map(s => s.trim()).filter(Boolean),
          specialties: formData.specialties
        },
        { withCredentials: true }
      );
      
      toast.success("Account created! Please upload verification documents.");
      onContractorLogin(response.data);
      navigate("/contractor/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Access qualified deal flow",
    "No lead chasing required",
    "Get paid for your expertise",
    "Build reputation over time"
  ];

  return (
    <div className="min-h-screen bg-black flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex w-1/2 bg-[#0A0A0A]/80 backdrop-blur-sm relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 to-transparent" />
        
        <div className="relative z-10 flex flex-col justify-center p-16">
          <div className="max-w-md">
            <img src="/logo.png" alt="DealForge" className="h-28 w-auto mb-8" />
            <h2 className="font-heading text-4xl font-black uppercase text-white mb-4">
              GROW YOUR CONTRACTING BUSINESS
            </h2>
            <p className="text-white/70 text-lg mb-8">
              Join the DealForge contractor network. Bid on verified deals from serious real estate investors. 
              No more lead chasing—just quality projects.
            </p>
            
            <div className="space-y-3 mb-8">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-gold/20 border border-gold/40 flex items-center justify-center">
                    <Check className="w-4 h-4 text-gold" />
                  </div>
                  <span className="text-white/70">{benefit}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-black/50 border border-white/[0.08] p-4">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-5 h-5 text-gold" />
                <span className="text-white font-bold">Verification Required</span>
              </div>
              <p className="text-white/70 text-sm">
                All contractors must be verified with valid business license and insurance before bidding.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 py-12 lg:px-16 overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <Link to="/" className="inline-flex items-center mb-8">
            <img src="/logo.png" alt="DealForge" className="h-20 w-auto" />
          </Link>

          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-gold/10 border border-gold/30 text-gold text-xs font-bold uppercase mb-4">
              <Briefcase className="w-4 h-4" />
              CONTRACTOR PORTAL
            </div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase mb-2">
              Join the Network
            </h1>
            <p className="text-white/70">
              $99/month after verification • Cancel anytime
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Step 1: Account Info */}
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label className="text-white/70">Business Name</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                    <Input
                      placeholder="ABC Contracting LLC"
                      value={formData.business_name}
                      onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                      className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white placeholder:text-white/70 h-12 rounded-none"
                      required
                      data-testid="business-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Contact Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                    <Input
                      placeholder="John Smith"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white placeholder:text-white/70 h-12 rounded-none"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                    <Input
                      type="email"
                      placeholder="contractor@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white placeholder:text-white/70 h-12 rounded-none"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                    <Input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white placeholder:text-white/70 h-12 rounded-none"
                      required
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                    <Input
                      type="password"
                      placeholder="Min 6 characters"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white placeholder:text-white/70 h-12 rounded-none"
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={() => {
                    if (!formData.business_name || !formData.contact_name || !formData.email || !formData.phone || !formData.password) {
                      toast.error("Please fill in all fields");
                      return;
                    }
                    setStep(2);
                  }}
                  className="w-full h-12 gold-gradient-btn rounded-none font-bold uppercase tracking-wide"
                  data-testid="next-step-btn"
                >
                  Next: Select Specialties
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </>
            )}

            {/* Step 2: Specialties */}
            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label className="text-white/70">Service Areas (Zip codes or cities, comma separated)</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                    <Input
                      placeholder="78701, Austin, Round Rock"
                      value={formData.service_areas}
                      onChange={(e) => setFormData({...formData, service_areas: e.target.value})}
                      className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white placeholder:text-white/70 h-12 rounded-none"
                      data-testid="service-areas-input"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-white/70">Specialties (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {specialtyOptions.map((specialty) => (
                      <button
                        key={specialty}
                        type="button"
                        onClick={() => toggleSpecialty(specialty)}
                        className={`px-3 py-2 text-sm text-left border transition-all duration-300 ${
                          formData.specialties.includes(specialty)
                            ? 'bg-gold/20 border-gold text-gold'
                            : 'bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white/70 hover:border-white/[0.15]'
                        }`}
                        data-testid={`specialty-${specialty.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {formData.specialties.includes(specialty) && (
                          <Check className="w-4 h-4 inline mr-2" />
                        )}
                        {specialty}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12 rounded-none border-white/[0.1] text-white/70 hover:bg-[#0A0A0A]/80 backdrop-blur-sm"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || formData.specialties.length === 0}
                    className="flex-1 h-12 gold-gradient-btn rounded-none font-bold uppercase tracking-wide"
                    data-testid="create-account-btn"
                  >
                    {loading ? "Creating..." : "Create Account"}
                  </Button>
                </div>
              </>
            )}
          </form>

          <p className="mt-6 text-center text-white/70 text-sm">
            Already have an account?{" "}
            <Link to="/contractor/login" className="text-gold hover:text-gold-light font-medium">
              Sign in
            </Link>
          </p>

          <p className="mt-4 text-center text-white/70 text-sm">
            Are you an investor?{" "}
            <Link to="/register" className="text-gold hover:text-gold-light font-medium">
              Register here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
